// "Get the app" prompt: who sees it, and remembering the dismissal.
//
// Audience = iPhone/iPad users on the WEBSITE. Anything served from
// fantasy.futbol is by definition not the native app (that runs from
// capacitor://localhost and Capacitor.isNativePlatform() is true there), so
// `isIOS && !isNativePlatform()` is exactly "iOS user, not in the app". The
// web cannot tell whether the app is *installed* - no browser API exposes it -
// so the copy stays neutral and the App Store link opens the app if present.
//
// Dismissal lives in localStorage (per browser). "Not now" snoozes for
// SNOOZE_DAYS; tapping through to the App Store dismisses permanently.
// The hand-off was VERIFIED on a real iPhone 2026-08-28: Apple 301-redirects
// apps.apple.com/app/id<id> to itms-appss:// for iOS user agents, and the
// Instagram in-app browser follows it into the App Store. A tap therefore
// means the user reached the listing and should not be asked again.
// iOS Safari can evict localStorage after ~7 idle days, in which case a
// long-absent user sees it once more - acceptable. try/catch for private mode.
import { Capacitor } from '@capacitor/core';
import {
  APP_PROMPT_STORAGE_KEY,
  serializeAppPromptSnooze,
  serializePermanentAppPromptDismissal,
  storedAppPromptAllowsDisplay,
} from '@/utils/appPromptStorage';

export const APP_STORE_ID: string = (import.meta.env.VITE_APP_STORE_ID as string | undefined) ?? '';
export const APP_STORE_URL = APP_STORE_ID
  ? `https://apps.apple.com/app/id${APP_STORE_ID}`
  : '';

export function isIOSWeb(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (Capacitor.isNativePlatform()) return false;
  const ua = navigator.userAgent || '';
  const isIPhone = /iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Macintosh; touch points distinguish it (Apple ships
  // no touch-screen Macs, so maxTouchPoints is 0 on a real Mac).
  const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return isIPhone || isIPad;
}

/** Raw record, or null when storage is empty or unavailable. */
function readStored(): string | null {
  try {
    return localStorage.getItem(APP_PROMPT_STORAGE_KEY);
  } catch {
    return null; // unavailable storage -> eligible, never stuck hidden
  }
}

/** True when the prompt should render for this visitor right now. */
export function shouldShowAppPrompt(): boolean {
  if (!APP_STORE_URL || !isIOSWeb()) return false;
  return storedAppPromptAllowsDisplay(readStored());
}

/** "Not now": ask again after SNOOZE_DAYS. */
export function snoozeAppPrompt(): void {
  try {
    localStorage.setItem(APP_PROMPT_STORAGE_KEY, serializeAppPromptSnooze());
  } catch { /* private mode - prompt may reappear next visit */ }
}

/** Tapped through to the App Store: never ask again in this browser. */
export function dismissAppPromptForever(): void {
  try {
    localStorage.setItem(APP_PROMPT_STORAGE_KEY, serializePermanentAppPromptDismissal());
  } catch { /* private mode - prompt may reappear next visit */ }
}
